import React, { Context, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Formik, FormikErrors, FormikTouched } from 'formik';

import { usePendingData } from './PendingData.provider';
import { FieldUtils } from '../hooks/useFields/types';

export function createMultiStepFormContext<T>(): Context<MultiStepFormCtx<T>> {
  return createContext<MultiStepFormCtx<T>>({
    values: {} as T,
    errors: {},
    touched: {},
    fields: {
      getCompletionPercentage: () => {
        throw new Error('getCompletionPercentage not implemented.');
      },
      getInitValues: () => {
        throw new Error('getInitValues not implemented.');
      },
      getErrorElementsBySection: () => {
        throw new Error('getErrorElementsBySection not implemented.');
      },
      getFieldNamesBySection: () => {
        throw new Error('getFieldNamesBySection not implemented.');
      },
      getRequiredFieldNamesBySection: () => {
        throw new Error('getRequiredFieldNamesBySection not implemented.');
      },
      getResetSubscriptions: () => {
        throw new Error('getResetSubscriptions not implemented.');
      },
      getSection: () => {
        throw new Error('getSection not implemented.');
      },
      getSectionIds: () => {
        throw new Error('getSectionIds not implemented.');
      },
      getValidationSchema: () => {
        throw new Error('getValidationSchema not implemented.');
      },
      hasError: () => {
        throw new Error('hasError not implemented.');
      },
      isRequiredField: () => {
        throw new Error('isRequiredField not implemented.');
      },
      getSerializedValues: () => {
        throw new Error('getSerializedValues not implemented.');
      },
    },
    handleBlur: () => {},
    handleFieldChange: () => {},
    handleAreaChange: () => {},
    setFieldTouched: () => {},
    handleToggleChange: () => {},
  });
}

export function MultiStepFormProvider<T>({
  context,
  fields,
  onSubmit,
  children,
}: {
  context: Context<MultiStepFormCtx<T>>;
  onSubmit: (data: T) => void;
  fields: FieldUtils<T>;
  children: React.JSX.Element;
}): React.JSX.Element {
  const ctx = useContext(context);
  const { initPendingData, updatePendingData } = usePendingData();
  const { getInitValues, getValidationSchema, getResetSubscriptions, getSerializedValues } = fields;
  const [isInitialized, setIsInitialized] = useState(false);

  const initialFormData = useMemo(() => getInitValues(), []);

  useEffect(() => {
    initPendingData(initialFormData);
  }, []);

  const resetSubscriptions = useMemo(() => getResetSubscriptions(), []);
  const [multiStepCtx, setMultiStepCtx] = useState(ctx);

  return (
    <Formik
      initialValues={initialFormData}
      validationSchema={getValidationSchema(multiStepCtx)}
      validateOnChange
      validateOnBlur
      onSubmit={(values) => {
        onSubmit(getSerializedValues(values));
      }}
    >
      {({
        errors,
        touched,
        values,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldTouched,
        setFieldError,
        setFieldValue,
      }) => {
        const handleCommonChange: CommonHandler<T> = (event, dataKey, value) => {
          updatePendingData({ [dataKey]: value });
          handleChange(event);

          if (String(value) !== String(values[dataKey])) handleResetOnUpdate(dataKey);
        };

        const handleCommonTextAreaChange: CommonTextAreaHandler<T> = (event, dataKey, value) => {
          updatePendingData({ [dataKey]: value });
          handleChange(event);

          if (String(value) !== String(values[dataKey])) handleResetOnUpdate(dataKey);
        };

        function handleResetOnUpdate(dataKey: keyof T) {
          /*  This is similar to pubsub in that any field that relies on another will be reset when that field changes.
              The filter is not 100% necessary, but performance can be poor without it on fields that have many subscribers. */
          const subscriptions = resetSubscriptions[dataKey] || [];
          const filtered = subscriptions.filter((subscription) => touched[subscription]);
          filtered?.forEach((subscription) => {
            setFieldValue(String(subscription), initialFormData[subscription]);
            setFieldError(String(subscription), undefined);
            setFieldTouched(String(subscription), false);
          });
        }

        function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>, { isEnabled }: ToggleChangeProps<T>) {
          const { name } = event.currentTarget;
          if (isKey(name) && typeof isEnabled === 'boolean') {
            setFieldTouched(name, true);
            setFieldValue(name, isEnabled);
          }
        }

        function handleFieldChange(event: React.ChangeEvent<HTMLInputElement>) {
          const { name, value } = event.currentTarget;

          if (isKey(name)) {
            /* setting these values for <Select /> use as that component lacks a normal blur (used to set touch values) */
            setFieldTouched(name, true);
            setFieldValue(name, value);
            handleCommonChange(event, name, value);
          }
        }

        function handleAreaChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
          const { name, value } = event.currentTarget;

          if (isKey(name)) {
            setFieldTouched(name, true);
            setFieldValue(name, value);
            handleCommonTextAreaChange(event, name, value);
          }
        }

        useEffect(() => {
          /*  storing the context in state in order to pass to the validation schema. 
              This allows validate() fns to make use of field values and pass ctx to other fns. */

          setMultiStepCtx({
            values,
            errors,
            touched,
            fields,
            handleBlur,
            handleFieldChange,
            handleAreaChange,
            setFieldTouched,
            handleToggleChange,
          });
          setIsInitialized(true);
        }, [values, touched, errors, fields]);

        return (
          <context.Provider value={multiStepCtx}>
            {isInitialized && <form onSubmit={handleSubmit}>{children}</form>}
          </context.Provider>
        );
      }}
    </Formik>
  );

  function isKey(_name?: string | number | symbol): _name is keyof T {
    return _name ? true : false;
  }
}

/* Type Definitions */

export type MultiStepFormCtx<T> = {
  values: T;
  errors: FormikErrors<T>;
  touched: FormikTouched<T>;
  fields: FieldUtils<T>;
  handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  handleFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAreaChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  setFieldTouched: (name: string, touched: boolean) => void;
  handleToggleChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    props: { id?: keyof T; isEnabled?: boolean },
  ) => void;
};

export type ToggleChangeProps<T> = {
  id?: keyof T;
  isEnabled?: boolean;
};

export type CommonHandler<T> = (
  event: React.ChangeEvent<HTMLInputElement>,
  dataKey: keyof T,
  value: string | boolean,
) => void;

export type CommonTextAreaHandler<T> = (
  event: React.ChangeEvent<HTMLTextAreaElement>,
  dataKey: keyof T,
  value: string | boolean,
) => void;

export type ToggleHandler<T> = (event: React.ChangeEvent<HTMLInputElement>, props: ToggleChangeProps<T>) => void;
